import { useState, useEffect } from 'react';
import { Card, Badge } from '@tremor/react';
import { Building, TrendingUp, RefreshCw, AlertCircle } from 'lucide-react';
import { RentCompsTable } from './RentCompsTable';
import { SaleCompsTable } from './SaleCompsTable';
import type { RentComp, SaleComp } from '../../types/calculations';

interface CompsSectionProps {
  propertyId: string;
  lat?: number;
  lng?: number;
}

interface RentCompsData {
  comps: RentComp[];
  median_rent: number;
  median_rent_per_sqft?: number;
  comp_count: number;
  rent_gap_percent?: number;
  data_source: string;
}

interface SaleCompsData {
  comps: SaleComp[];
  median_price: number;
  median_price_per_sqft?: number;
  comp_count: number;
  arv_comparison?: {
    subject_value: number;
    comp_median: number;
    premium_percent: number;
  };
  data_source: string;
}

export function CompsSection({ propertyId }: CompsSectionProps) {
  const [rentComps, setRentComps] = useState<RentCompsData | null>(null);
  const [saleComps, setSaleComps] = useState<SaleCompsData | null>(null);
  const [rentLoading, setRentLoading] = useState(true);
  const [saleLoading, setSaleLoading] = useState(true);
  const [rentError, setRentError] = useState<string | null>(null);
  const [saleError, setSaleError] = useState<string | null>(null);

  const fetchRentComps = async () => {
    // Validate propertyId before making API call
    if (!propertyId || propertyId === 'undefined' || propertyId === 'null') {
      setRentError('Invalid property ID');
      setRentLoading(false);
      return;
    }

    setRentLoading(true);
    setRentError(null);
    try {
      const response = await fetch(`/api/comps/rent/${propertyId}`);
      if (!response.ok) {
        // Try to get detailed error from response body
        let errorDetail = response.statusText;
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorData.message || errorDetail;
        } catch {
          // Ignore JSON parse errors
        }
        throw new Error(`${errorDetail}`);
      }
      const data = await response.json();
      setRentComps(data);
    } catch (err) {
      console.error('Failed to fetch rent comps:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to load rent comparables';
      // Provide helpful message for common errors
      if (errorMsg.includes('missing coordinates')) {
        setRentError('Property missing coordinates - cannot fetch nearby comps');
      } else if (errorMsg.includes('not found')) {
        setRentError('Property not found in database');
      } else if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
        setRentError('Network error - please check your connection and try again');
      } else {
        setRentError(errorMsg);
      }
    } finally {
      setRentLoading(false);
    }
  };

  const fetchSaleComps = async () => {
    // Validate propertyId before making API call
    if (!propertyId || propertyId === 'undefined' || propertyId === 'null') {
      setSaleError('Invalid property ID');
      setSaleLoading(false);
      return;
    }

    setSaleLoading(true);
    setSaleError(null);
    try {
      const response = await fetch(`/api/comps/sale/${propertyId}`);
      if (!response.ok) {
        // Try to get detailed error from response body
        let errorDetail = response.statusText;
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorData.message || errorDetail;
        } catch {
          // Ignore JSON parse errors
        }
        throw new Error(`${errorDetail}`);
      }
      const data = await response.json();
      setSaleComps(data);
    } catch (err) {
      console.error('Failed to fetch sale comps:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to load sale comparables';
      // Provide helpful message for common errors
      if (errorMsg.includes('missing coordinates')) {
        setSaleError('Property missing coordinates - cannot fetch nearby comps');
      } else if (errorMsg.includes('not found')) {
        setSaleError('Property not found in database');
      } else if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
        setSaleError('Network error - please check your connection and try again');
      } else {
        setSaleError(errorMsg);
      }
    } finally {
      setSaleLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if we have a valid propertyId
    if (propertyId && propertyId !== 'undefined' && propertyId !== 'null') {
      fetchRentComps();
      fetchSaleComps();
    }
  }, [propertyId]);

  // Split rent comps by renovation status
  const renovatedRentComps = rentComps?.comps.filter(c => c.renovation_status === 'renovated') || [];
  const unrenovatedRentComps = rentComps?.comps.filter(c => c.renovation_status === 'unrenovated') || [];
  const unknownRentComps = rentComps?.comps.filter(c => c.renovation_status === 'unknown') || [];

  // Convert sale comps to expected format
  const saleCompsForTable: SaleComp[] = saleComps?.comps.map(c => ({
    address: c.address,
    sale_price: c.sale_price,
    sqft: c.sqft,
    sale_date: c.sale_date || '',
    distance_miles: c.distance_miles,
    price_per_sqft: c.price_per_sqft,
    bedrooms: c.bedrooms,
    bathrooms: c.bathrooms,
    year_built: c.year_built,
  })) || [];

  const isLoading = rentLoading || saleLoading;
  const hasError = rentError || saleError;

  return (
    <Card className="mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Building className="w-5 h-5 text-blue-600" />
          <h3 className="text-tremor-title font-medium text-tremor-content-strong">
            Market Comparables
          </h3>
        </div>
        {!isLoading && (
          <div className="flex items-center gap-2">
            {rentComps && (
              <Badge color="blue" size="xs">
                {rentComps.comp_count} Rent Comps
              </Badge>
            )}
            {saleComps && (
              <Badge color="green" size="xs">
                {saleComps.comp_count} Sale Comps
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          <span>Loading comparables...</span>
        </div>
      )}

      {/* Error State */}
      {!isLoading && hasError && (
        <div className="space-y-3">
          {rentError && (
            <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">Rent comps: {rentError}</span>
              </div>
              <button
                onClick={fetchRentComps}
                className="text-sm text-red-600 hover:text-red-800 underline"
              >
                Retry
              </button>
            </div>
          )}
          {saleError && (
            <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">Sale comps: {saleError}</span>
              </div>
              <button
                onClick={fetchSaleComps}
                className="text-sm text-red-600 hover:text-red-800 underline"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      )}

      {/* Rent Comps Section */}
      {!rentLoading && !rentError && rentComps && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            <h4 className="text-sm font-medium text-gray-700">Rental Comparables</h4>
            {rentComps.rent_gap_percent !== undefined && (
              <Badge
                color={rentComps.rent_gap_percent > 0 ? 'green' : 'red'}
                size="xs"
              >
                {rentComps.rent_gap_percent > 0 ? '+' : ''}
                {(rentComps.rent_gap_percent * 100).toFixed(1)}% vs market
              </Badge>
            )}
            <Badge color="gray" size="xs">
              {rentComps.data_source}
            </Badge>
          </div>

          {/* Summary Stats */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Median Rent: <span className="font-semibold text-gray-900">${rentComps.median_rent.toLocaleString()}/mo</span></span>
              {rentComps.median_rent_per_sqft && (
                <span className="text-gray-600">Median $/SqFt: <span className="font-semibold text-gray-900">${rentComps.median_rent_per_sqft.toFixed(2)}</span></span>
              )}
            </div>
          </div>

          {/* Renovated Comps */}
          {renovatedRentComps.length > 0 && (
            <RentCompsTable
              comps={renovatedRentComps}
              title="Renovated Units"
              renovationStatus="renovated"
            />
          )}

          {/* Unrenovated Comps */}
          {unrenovatedRentComps.length > 0 && (
            <RentCompsTable
              comps={unrenovatedRentComps}
              title="Unrenovated Units"
              renovationStatus="unrenovated"
            />
          )}

          {/* Unknown Status Comps - show as unrenovated for display */}
          {unknownRentComps.length > 0 && (
            <RentCompsTable
              comps={unknownRentComps}
              title="Unknown Status"
              renovationStatus="unrenovated"
            />
          )}

          {/* No renovation classification - show all comps */}
          {renovatedRentComps.length === 0 && unrenovatedRentComps.length === 0 && unknownRentComps.length === 0 && rentComps.comps.length > 0 && (
            <RentCompsTable
              comps={rentComps.comps}
              title="All Rental Comps"
              renovationStatus="unrenovated"
            />
          )}
        </div>
      )}

      {/* Sale Comps Section */}
      {!saleLoading && !saleError && saleComps && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <SaleCompsTable
            comps={saleCompsForTable}
            subjectValue={saleComps.arv_comparison?.subject_value}
            medianPrice={saleComps.median_price}
          />
          <div className="mt-2 text-sm text-gray-500">
            Data source: {saleComps.data_source}
          </div>
        </div>
      )}

      {/* No Data State */}
      {!isLoading && !hasError && !rentComps?.comp_count && !saleComps?.comp_count && (
        <div className="text-center py-8 text-gray-500">
          <Building className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p>No comparables found in the area.</p>
        </div>
      )}
    </Card>
  );
}
